import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

interface Project {
  id: string;
  title: string;
  category: string;
  image: string;
  link: string;
  color: string;
}

const PortfolioGrid = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.5]);
  const isInView = useInView(heroRef, { once: true, amount: 0.2 });

  const projects: Project[] = [
    {
      id: "1",
      title: "Shopify Development",
      category: "E-commerce",
      image: "/coding.jpg",
      link: "/shopify",
      color: "#ffd54f"
    },
    {
      id: "2",
      title: "SEO",
      category: "SEO",
      image: "/branding.png",
      link: "/seo",
      color: "#81deea"
    },
    {
      id: "3",
      title: "Email and SMS Marketing",
      category: "Marketing",
      image: "/digitalMarketing.jpg",
      link: "/email",
      color: "#ffd54f"
    },
    {
      id: "4",
      title: "Content Creation",
      category: "Content",
      image: "/content.jpg",
      link: "/fb",
      color: "#81deea"
    },
    {
      id: "5",
      title: "Content Strategy",
      category: "Strategize",
      image: "/strategy.jpg",
      link: "/strategy",
      color: "#ffd54f"
    },
    {
      id: "6",
      title: "Why Groviyo",
      category: "Why?",
      image: "/why.jpg",
      link: "/why",
      color: "#81deea"
    }
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Throttle mouse movement updates
      requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Simplified animations with better performance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#b3ebf2] to-[#ffd54f] py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Simplified Background Effects */}
      <div className="absolute inset-0 z-0">
        <motion.div style={{ opacity }} className="w-full h-full absolute inset-0" />
        {[...Array(2)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full mix-blend-overlay"
            animate={{
              x: [0, 20, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              delay: i * 3,
              ease: "linear"
            }}
            style={{
              background: `radial-gradient(circle, ${projects[0].color}${0.04 + i * 0.02} 0%, transparent 70%)`,
              width: `${700 + i * 200}px`,
              height: `${700 + i * 200}px`,
              left: `${25 + i * 10}%`,
              top: `${25 + i * 10}%`,
              filter: 'blur(60px)',
            }}
          />
        ))}
      </div>

      {/* Optimized Mouse Follow Effect */}
      <motion.div 
        className="fixed inset-0 pointer-events-none z-0 hidden lg:block"
        animate={{
          background: `radial-gradient(800px at ${mousePosition.x}px ${mousePosition.y}px, 
                      rgba(255, 213, 79, 0.1), transparent 70%)`
        }}
        transition={{ duration: 0.2 }}
      />

      <motion.div
        ref={heroRef}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-7xl mx-auto relative z-10"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              className="relative group"
              onHoverStart={() => setHoveredId(project.id)}
              onHoverEnd={() => setHoveredId(null)}
            >
              <motion.a
                href={project.link}
                className="block relative h-64 sm:h-80 w-full rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm"
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
              >
                {/* Optimized overlay transitions */}
                <div
                  className="absolute inset-0 bg-black/40 z-10 transition-opacity duration-300"
                  style={{ opacity: hoveredId === project.id ? 1 : 0 }}
                />
                
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="h-full w-full object-cover transition-transform duration-700"
                  style={{
                    scale: hoveredId === project.id ? 1.1 : 1
                  }}
                />

                <div
                  className="absolute inset-0 z-20 p-6 flex flex-col justify-between transition-all duration-300"
                  style={{
                    opacity: hoveredId === project.id ? 1 : 0,
                    transform: `translateY(${hoveredId === project.id ? 0 : 10}px)`
                  }}
                >
                  <span
                    className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white w-fit"
                    style={{ backgroundColor: project.color }}
                  >
                    {project.category}
                  </span>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {project.title}
                    </h3>
                  </div>
                </div>

                <div
                  className="absolute inset-0 z-0 transition-opacity duration-300"
                  style={{
                    background: `linear-gradient(45deg, ${project.color}40, transparent)`,
                    opacity: hoveredId === project.id ? 1 : 0
                  }}
                />
              </motion.a>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default PortfolioGrid;